import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/auth/AuthProvider';

export const useAudioRecording = (onTranscriptionComplete?: (text: string) => void) => {
  const { user } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        transcribeAudio();
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Recording stopped, transcribing...');
    }
  }, [isRecording]);

  const transcribeAudio = useCallback(async () => {
    if (audioChunksRef.current.length === 0) return;
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    setIsTranscribing(true);
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.wav');
      formData.append('userId', user.id);
      formData.append('language', 'en');

      const { data, error } = await supabase.functions.invoke('transcribe-audio', {
        body: formData
      });

      if (error) {
        throw new Error(error.message || 'Failed to transcribe audio');
      }

      if (!data.success || !data.text) {
        throw new Error(data.error || 'Failed to transcribe audio');
      }

      toast.success('Audio transcribed and ready!');
      onTranscriptionComplete?.(data.text);
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Failed to transcribe audio. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  }, [user?.id, onTranscriptionComplete]);

  return {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
  };
};
